import { Constants } from '@babylonjs/core/Engines/constants';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import '@babylonjs/core/Meshes/thinInstanceMesh';
import type { Scene } from '@babylonjs/core/scene';
import { setInstances } from '../../map/map-scene.js';
import { CreateDisc } from '@babylonjs/core/Meshes/Builders/discBuilder';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { overlayMaterial } from '../../map/map-scene.js';
import { FIRE_POOL, GLOW } from './building-config.js';
import {
  buildBuildingModel,
  levelScale,
  modelKey,
  type BuildingLook,
  type BuildingModel,
} from './building-models.js';

// Draws every building in a scene cheaply (CLAUDE.md rule 8): one mesh per
// building look (a lit and an out Hearthfire are two), shared by every
// building of that look and drawn with thin instances, so draw calls grow
// with the kinds of building, not their number. Nothing here animates: a
// still home base never wakes the renderer (tech spec §6). After `set`, the
// caller redraws (`Stage.invalidate`).

export interface BuildingPlacement {
  readonly buildingId: string;
  /** Its level (each has its own model); 1 when left out. */
  readonly level?: number;
  /** Hearthfires: lit or out. Null for buildings that are never lit. */
  readonly lit: boolean | null;
  /** A resource building napping (unpaid upkeep, a cut-off homestead, #601): its windows go dark. */
  readonly asleep?: boolean;
  readonly x: number;
  readonly z: number;
  /** Ground height. */
  readonly y?: number;
  readonly scale?: number;
  /** Heading in radians; 0 faces −z (towards the default camera). */
  readonly yaw?: number;
}

export interface BuildingFieldStats {
  readonly buildings: number;
  /** Meshes with at least one instance (bodies and glows): the field's draw calls. */
  readonly meshes: number;
  /** Fires drawn lit. */
  readonly lit: number;
}

/** How far the light pool floats over a building's ground, per unit of building scale. */
const POOL_LIFT = 0.06; // TUNE: clears the home tiles' dome

const lookOf = (p: Pick<BuildingPlacement, 'lit' | 'asleep'>): BuildingLook =>
  p.lit === null ? (p.asleep ? 'out' : 'plain') : p.lit ? 'lit' : 'out';

export class BuildingField {
  readonly #scene: Scene;
  readonly #models = new Map<string, BuildingModel>();
  readonly #body: PBRMaterial;
  readonly #glow: StandardMaterial;
  /** The soft pool of light under every lit fire: one mesh, one instance per fire. */
  readonly #pool: Mesh;
  #stats: BuildingFieldStats = { buildings: 0, meshes: 0, lit: 0 };

  constructor(scene: Scene) {
    this.#scene = scene;
    const body = new PBRMaterial('building-vinyl', scene);
    body.albedoColor = Color3.White(); // vertex colours multiply this
    body.metallic = 0;
    body.roughness = 0.45; // TUNE: same soft vinyl as map props
    body.clearCoat.isEnabled = true;
    body.clearCoat.intensity = 0.8;
    body.clearCoat.roughness = 0.2;
    this.#body = body;
    const glow = new StandardMaterial('building-glow', scene);
    glow.disableLighting = true;
    glow.diffuseColor = Color3.Black();
    glow.specularColor = Color3.Black();
    glow.emissiveColor = new Color3(GLOW, GLOW, GLOW); // vertex colours tint it
    glow.alphaMode = Constants.ALPHA_DISABLE;
    this.#glow = glow;
    this.#pool = firePool(scene);
  }

  /** Replaces every building drawn with these. */
  set(placements: readonly BuildingPlacement[]): void {
    const byKey = new Map<
      string,
      { buildingId: string; look: BuildingLook; level: number; matrices: Matrix[] }
    >();
    const turn = new Quaternion();
    let lit = 0;
    const pools: Matrix[] = [];
    for (const p of placements) {
      const look = lookOf(p);
      const level = p.level ?? 1;
      const key = modelKey(p.buildingId, look, level);
      if (p.lit === true) lit++;
      let group = byKey.get(key);
      if (!group) {
        byKey.set(key, (group = { buildingId: p.buildingId, look, level, matrices: [] }));
      }
      const list = group.matrices;
      Quaternion.RotationYawPitchRollToRef(p.yaw ?? 0, 0, 0, turn);
      const s = p.scale ?? 1;
      list.push(
        Matrix.Compose(new Vector3(s, s, s), turn.clone(), new Vector3(p.x, p.y ?? 0, p.z)),
      );
      if (p.lit === true) {
        const r = s * levelScale(level).across;
        pools.push(
          // Lifted over the tile's domed top, which would hide its middle.
          Matrix.Compose(
            new Vector3(r, 1, r),
            Quaternion.Identity(),
            new Vector3(p.x, (p.y ?? 0) + POOL_LIFT * s, p.z),
          ),
        );
      }
    }
    for (const [key, { buildingId, look, level }] of byKey) {
      if (this.#models.has(key)) continue;
      const model = buildBuildingModel(this.#scene, buildingId, look, level);
      model.body.material = this.#body;
      if (model.glow) model.glow.material = this.#glow;
      this.#models.set(key, model);
    }
    let meshes = 0;
    for (const [key, model] of this.#models) {
      const matrices = byKey.get(key)?.matrices ?? [];
      for (const mesh of [model.body, model.glow]) {
        if (!mesh) continue;
        setInstances(mesh, matrices, true);
        if (matrices.length > 0) meshes++;
      }
    }
    setInstances(this.#pool, pools, true);
    if (pools.length > 0) meshes++;
    this.#stats = { buildings: placements.length, meshes, lit };
  }

  /**
   * The model's own box for a placement already `set` (local space, before
   * its placement's move and scale), body and glow together, or null when
   * it isn't drawn. The portrait camera fits the whole building, flat water
   * and ground pieces too, from this (#621 W1-10c).
   */
  modelBounds(
    p: Pick<BuildingPlacement, 'buildingId' | 'lit' | 'asleep' | 'level'>,
  ): { min: Vector3; max: Vector3 } | null {
    const model = this.#models.get(modelKey(p.buildingId, lookOf(p), p.level ?? 1));
    if (!model) return null;
    const min = new Vector3(Infinity, Infinity, Infinity);
    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const mesh of [model.body, model.glow]) {
      if (!mesh) continue;
      mesh.refreshBoundingInfo();
      const box = mesh.getBoundingInfo().boundingBox;
      min.minimizeInPlace(box.minimum);
      max.maximizeInPlace(box.maximum);
    }
    return min.x <= max.x ? { min, max } : null;
  }

  get stats(): BuildingFieldStats {
    return this.#stats;
  }

  dispose(): void {
    for (const model of this.#models.values()) {
      model.body.dispose();
      model.glow?.dispose();
    }
    this.#models.clear();
    this.#body.dispose();
    this.#glow.dispose();
    // The pool owns its overlay material, so it goes too.
    this.#pool.dispose(false, true);
  }
}

/**
 * A flat disc of warm light, brightest in the middle and fading to nothing
 * at its rim (vertex alpha), drawn unlit and blended over the ground.
 */
function firePool(scene: Scene): Mesh {
  const disc = CreateDisc(
    'building-fire-pool',
    { radius: FIRE_POOL.radius, tessellation: 40 },
    scene,
  );
  disc.rotation.x = Math.PI / 2;
  disc.bakeCurrentTransformIntoVertices();
  const positions = disc.getVerticesData(VertexBuffer.PositionKind) ?? [];
  const warm = Color3.FromHexString(FIRE_POOL.color).toLinearSpace();
  const colors = new Float32Array((positions.length / 3) * 4);
  for (let i = 0; i < positions.length / 3; i++) {
    const d = Math.hypot(positions[i * 3] ?? 0, positions[i * 3 + 2] ?? 0) / FIRE_POOL.radius;
    const fade = Math.max(0, 1 - d);
    colors.set([warm.r, warm.g, warm.b, FIRE_POOL.alpha * fade * fade], i * 4);
  }
  disc.setVerticesData(VertexBuffer.ColorKind, colors);
  disc.hasVertexAlpha = true;
  const material = overlayMaterial(scene, 'building-fire-pool-mat');
  material.alphaMode = Constants.ALPHA_ADD; // light adds to the ground under it
  disc.material = material;
  disc.isPickable = false;
  disc.alwaysSelectAsActiveMesh = true;
  disc.setEnabled(false);
  return disc;
}
